import {
  Box,
  Text,
  Select,
  Input,
  InputGroup,
  InputLeftAddon,
  Icon,
  Image,
  Switch,
  Checkbox,
  Divider,
  VStack,
  Button,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import puntosColombiaLogo from '../assets/puntos-colombia-logo.svg';
import { useContext, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import { PaymentInformation } from '../types';
import PaymentMethodElement from './PaymentMethodElement';
import PaymentSummaryTotalAmount from './PaymentSummaryTotalAmount';
import { pseBanks, bankNames } from '@/data/banks';
import type { PseBank } from '@src/backend/src/common/data/banks';
import _ from 'lodash';
import PersonalInfo from './PersonalInfo';
import StepperContext from '../contexts/StepperContext';
import MerchantContext from '../contexts/MerchantContext';
import { clearSavedPayer, readSavedPayer, writeSavedPayer } from '../common/savedPayerInfo';
import { analyzePaymentForm, PayerDocumentType, PaymentInformationWarnings } from '../common/paymentFormValidation';
import { getVisiblePaymentMethodsForInvoice } from '../helpers/paymentMethods';

// Remembers, per merchant, that the payer explicitly chose PSE over Botón
// Bancolombia. Kept at module scope so the choice survives PaymentForm
// unmount/remount (e.g. stepping back and forth), where a ref would reset and
// saved payer info would otherwise re-trigger the auto-redirect.
const preferPseOverBancolombiaByMerchant = new Map<string, boolean>();

const PaymentForm = (): JSX.Element => {
  const [pseBank, setPseBank] = useState<PseBank | undefined>(undefined);
  const [name, setName] = useState<string>('');
  const [documentType, setDocumentType] = useState<PayerDocumentType>(undefined);
  const [documentNumber, setDocumentNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [cardNumber, setCardNumber] = useState<string>('');
  const [cardExpDate, setCardExpDate] = useState<string>('');
  const [cardCVC, setCardCVC] = useState<string>('');
  const [installmentsEnabled, setInstallmentsEnabled] = useState<boolean>(false);
  const [installments, setInstallments] = useState<number>(1);
  const [cardFeeConfirmation, setCardFeeConfirmation] = useState<boolean>(false);
  const [rememberPayerInfo, setRememberPayerInfo] = useState<boolean>(false);
  const [puntosColombiaDocumentType, setPuntosColombiaDocumentType] = useState<'cc' | 'ce' | 'nit' | undefined>(
    undefined
  );
  const [puntosColombiaDocumentNumber, setPuntosColombiaDocumentNumber] = useState<string>('');
  // True only when the auto-redirect actually moved the payer from PSE to Botón
  // Bancolombia, so we offer a way back — but not when they chose it themselves.
  const [didRedirectToBancolombiaButton, setDidRedirectToBancolombiaButton] = useState<boolean>(false);

  const { totalAmountToPay, invoicesToPay } = useContext(InvoicesToPayContext);
  const { paymentInformationContext, paymentMethodContext } = useContext(StepperContext);
  const { setPaymentInformation } = paymentInformationContext;
  const { paymentMethod, setPaymentMethod } = paymentMethodContext;
  const { merchant } = useContext(MerchantContext);
  const { subscriptionToCharge, totalSubscriptionAmount, offer, customerSubscription } =
    useContext(SubscriptionsContext);
  const requiresPuntosColombia =
    subscriptionToCharge !== null && (customerSubscription?.puntosColombia ?? offer?.puntosColombia ?? 0) > 0;

  const availableMethods = useMemo(
    () =>
      getVisiblePaymentMethodsForInvoice(
        merchant,
        invoicesToPay,
        totalAmountToPay + totalSubscriptionAmount,
        totalSubscriptionAmount > 0
      ),
    [merchant, invoicesToPay, totalAmountToPay, totalSubscriptionAmount]
  );
  const bancolombiaButtonMethod = availableMethods.find((method) => method.id === 'bancolombiaButton');
  const pseMethod = availableMethods.find((method) => method.id === 'pse');

  // When the merchant prefers Botón Bancolombia, a CC/CE payer who picks PSE +
  // Bancolombia (and whose amount fits under the Botón Bancolombia limit, i.e.
  // the method is available) should be nudged onto the cheaper rail. NIT and
  // passport are excluded (Botón Bancolombia does not support them).
  const bancolombiaButtonIsBetterThanPse =
    !!merchant?.bancolombiaButtonPreferred &&
    pseBank === 'bancolombia' &&
    (documentType === 'cc' || documentType === 'ce') &&
    !!bancolombiaButtonMethod;

  const showRevertToPse =
    paymentMethod?.id === 'bancolombiaButton' &&
    didRedirectToBancolombiaButton &&
    bancolombiaButtonIsBetterThanPse &&
    !!pseMethod;

  useEffect(() => {
    if (!requiresPuntosColombia) return;
    const invoice = invoicesToPay[0];
    if (!invoice) return;
    setPuntosColombiaDocumentNumber((current) => current || invoice.customerDocumentNumber);
    const invoiceType = invoice.customerDocumentType;
    if (invoiceType === 'cc' || invoiceType === 'ce' || invoiceType === 'nit')
      setPuntosColombiaDocumentType((current) => current ?? invoiceType);
  }, [requiresPuntosColombia, invoicesToPay]);

  const analysis = useMemo(
    () =>
      analyzePaymentForm({
        paymentMethod,
        merchant,
        pseBank,
        name,
        documentType,
        documentNumber,
        email,
        phoneNumber,
        cardNumber,
        cardExpDate,
        cardCVC,
        installments,
        cardFeeConfirmation,
        requiresPuntosColombia,
        puntosColombiaDocumentType,
        puntosColombiaDocumentNumber,
      }),
    [
      paymentMethod,
      merchant,
      pseBank,
      name,
      documentType,
      documentNumber,
      email,
      phoneNumber,
      cardNumber,
      cardExpDate,
      cardCVC,
      installments,
      cardFeeConfirmation,
      requiresPuntosColombia,
      puntosColombiaDocumentType,
      puntosColombiaDocumentNumber,
    ]
  );

  const [displayedWarnings, setDisplayedWarnings] = useState<PaymentInformationWarnings>(analysis.warnings);

  useEffect(() => {
    const t = setTimeout(() => setDisplayedWarnings(analysis.warnings), 1500);
    return () => clearTimeout(t);
  }, [analysis.warnings]);

  // Load saved payer info before paint so an eligible PSE + Bancolombia payer is
  // redirected to Botón Bancolombia without first flashing the PSE form.
  useLayoutEffect(() => {
    const id = merchant?.id;
    if (!id) return;
    const saved = readSavedPayer(id);
    if (!saved) return;
    if (saved.name !== undefined) setName(saved.name);
    if (saved.documentType !== undefined) setDocumentType(saved.documentType);
    if (saved.documentNumber !== undefined) setDocumentNumber(saved.documentNumber);
    if (saved.email !== undefined) setEmail(saved.email);
    if (saved.phoneNumber !== undefined) setPhoneNumber(saved.phoneNumber);
    if (saved.pseBank !== undefined) setPseBank(saved.pseBank);
    if (saved.puntosColombiaDocumentType !== undefined) setPuntosColombiaDocumentType(saved.puntosColombiaDocumentType);
    if (saved.puntosColombiaDocumentNumber !== undefined)
      setPuntosColombiaDocumentNumber(saved.puntosColombiaDocumentNumber);
    setRememberPayerInfo(true);
    // A returning eligible payer is redirected before paint (avoiding a PSE
    // flash), unless they previously chose to stay on PSE for this merchant.
    if (
      paymentMethod?.id === 'pse' &&
      merchant?.bancolombiaButtonPreferred &&
      saved.pseBank === 'bancolombia' &&
      (saved.documentType === 'cc' || saved.documentType === 'ce') &&
      bancolombiaButtonMethod &&
      preferPseOverBancolombiaByMerchant.get(id) !== true
    ) {
      setDidRedirectToBancolombiaButton(true);
      setPaymentMethod(bancolombiaButtonMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant?.id]);

  const debouncedPersistPayer = useMemo(
    () =>
      _.debounce((merchantId: string, fields: Parameters<typeof writeSavedPayer>[1]) => {
        writeSavedPayer(merchantId, fields);
      }, 400),
    []
  );

  useEffect(() => () => debouncedPersistPayer.cancel(), [debouncedPersistPayer]);

  useEffect(() => {
    if (!rememberPayerInfo || !merchant?.id) return;
    debouncedPersistPayer(merchant.id, {
      name,
      documentType,
      documentNumber,
      email,
      phoneNumber: phoneNumber || undefined,
      pseBank,
      puntosColombiaDocumentType,
      puntosColombiaDocumentNumber: puntosColombiaDocumentNumber || undefined,
    });
  }, [
    rememberPayerInfo,
    merchant?.id,
    name,
    documentType,
    documentNumber,
    email,
    phoneNumber,
    pseBank,
    puntosColombiaDocumentType,
    puntosColombiaDocumentNumber,
    debouncedPersistPayer,
  ]);

  useEffect(() => {
    setPaymentInformation(analysis.paymentInformation);
  }, [analysis.paymentInformation, setPaymentInformation]);

  // Redirect an eligible PSE + Bancolombia payer to Botón Bancolombia, unless
  // they've explicitly chosen to stay on PSE. This is triggered directly from
  // the bank / document-type change handlers (edge-triggered) rather than a
  // continuous effect, so it fires regardless of the order the payer fills the
  // fields in and never bounces them back after they opt out. The "prefer PSE"
  // choice lives in a shared, per-merchant store so it survives remounts.
  const maybeRedirectToBancolombiaButton = (nextPseBank: PseBank | undefined, nextDocumentType: PayerDocumentType) => {
    if (paymentMethod?.id !== 'pse') return;
    const prefersPse = !!merchant?.id && preferPseOverBancolombiaByMerchant.get(merchant.id) === true;
    if (
      merchant?.bancolombiaButtonPreferred &&
      nextPseBank === 'bancolombia' &&
      (nextDocumentType === 'cc' || nextDocumentType === 'ce') &&
      bancolombiaButtonMethod &&
      !prefersPse
    ) {
      setDidRedirectToBancolombiaButton(true);
      setPaymentMethod(bancolombiaButtonMethod);
    }
  };

  const handlePseBankChange = (bank: PseBank) => {
    setPseBank(bank);
    // Reselecting a non-Bancolombia bank clears the "stay on PSE" choice, so
    // picking Bancolombia again re-arms the redirect.
    if (bank !== 'bancolombia') {
      if (merchant?.id) preferPseOverBancolombiaByMerchant.delete(merchant.id);
      return;
    }
    maybeRedirectToBancolombiaButton(bank, documentType);
  };

  const handlePseDocumentTypeChange = (docType: PayerDocumentType) => {
    setDocumentType(docType);
    maybeRedirectToBancolombiaButton(pseBank, docType);
  };

  const paymentInformationHandler =
    (field: keyof PaymentInformation) =>
    (event: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (field === 'cardNumber') {
        const cardNums = value.replace(/\D/g, '');
        let formattedValue = '';

        for (let i = 0; i < cardNums.length; i++) {
          if (i > 0 && i % 4 === 0 && i < 16) {
            formattedValue += '-';
          }
          formattedValue += cardNums[i];
        }

        if (cardNums.length <= 16) {
          setCardNumber(formattedValue);
        }
      }
      const digits = value.replace(/\D/g, '');
      if (field === 'cardExpDate' && /^\d*$/.test(digits) && (digits.length <= 4 || digits.length === 6)) {
        setCardExpDate(digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2).slice(-2)}` : digits);
      }
      if (field === 'cardCVC' && /^\d*$/.test(value) && value.length <= 4) {
        setCardCVC(value);
      }
    };

  if (!paymentMethod) return <></>;

  return (
    <Box width={'100%'} gap={4}>
      <Box borderBottom={'1px solid'} borderColor={'#DFDEE2'}>
        <PaymentSummaryTotalAmount compact />
      </Box>
      <Box display={'flex'} flexDirection={'column'} width={'100%'} pt={6} gap={4}>
        <PaymentMethodElement />
        {paymentMethod.id === 'pse' && (
          <>
            <Text fontWeight={600}>Entidad Bancaria</Text>
            <Select
              placeholder="Seleccionar"
              variant={'bankSelect'}
              onChange={(val) => handlePseBankChange(val.target.value as PseBank)}
              value={pseBank}
              isRequired
            >
              {_.sortBy(pseBanks, (bank) => bankNames[bank].name).map((bank) => (
                <option value={bank} style={{ color: '#3C3A45' }} key={bank}>
                  {bankNames[bank].name}
                </option>
              ))}
            </Select>
            <PersonalInfo
              name={name}
              setName={setName}
              documentType={documentType}
              setDocumentType={handlePseDocumentTypeChange}
              documentNumber={documentNumber}
              setDocumentNumber={setDocumentNumber}
              email={email}
              setEmail={setEmail}
              warnings={displayedWarnings}
            />
          </>
        )}
        {paymentMethod.id === 'nequiButton' && (
          <>
            <FormControl isInvalid={!!displayedWarnings.phoneNumber}>
              <Text fontWeight={600}>Número de Celular</Text>
              <InputGroup>
                <InputLeftAddon fontWeight={600} color={'rentals.primary.btn'}>
                  +57
                </InputLeftAddon>
                <Input
                  variant={'bankSelect'}
                  placeholder="Número de Celular"
                  onChange={(val) =>
                    /^\d*$/.test(val.target.value) && val.target.value.length <= 10 && setPhoneNumber(val.target.value)
                  }
                  value={phoneNumber}
                />
              </InputGroup>
              <FormErrorMessage>{displayedWarnings.phoneNumber}</FormErrorMessage>
            </FormControl>
            <PersonalInfo
              name={name}
              setName={setName}
              documentType={documentType}
              setDocumentType={setDocumentType}
              documentNumber={documentNumber}
              setDocumentNumber={setDocumentNumber}
              email={email}
              setEmail={setEmail}
              warnings={displayedWarnings}
            />
          </>
        )}
        {paymentMethod.id === 'bancolombiaButton' && (
          <>
            {totalAmountToPay > 2400000 && (
              <Box display={'flex'} flexDirection={'row'} alignItems={'flex-start'} p={1} gap={2}>
                <Icon color={'rentals.states.processing'} boxSize={4} mt={1} />
                <Text
                  fontSize={'0.875rem'}
                >{`El tope del Botón Bancolombia es $2.4MM COP. Si ya lo aumentaste en la Sucursal Virtual, continúa. De lo contrario, ajusta el tope o paga por PSE.`}</Text>
              </Box>
            )}
            <PersonalInfo
              name={name}
              setName={setName}
              documentType={documentType}
              setDocumentType={setDocumentType}
              documentNumber={documentNumber}
              setDocumentNumber={setDocumentNumber}
              email={email}
              setEmail={setEmail}
              warnings={displayedWarnings}
            />
            {showRevertToPse && pseMethod && (
              <Button
                variant={'link'}
                size={'sm'}
                alignSelf={'flex-start'}
                fontWeight={500}
                color={'gray.600'}
                textDecoration={'underline'}
                textUnderlineOffset={'3px'}
                textDecorationThickness={'1px'}
                textDecorationColor={'gray.400'}
                _hover={{ color: 'gray.800', textDecorationColor: 'gray.800' }}
                onClick={() => {
                  if (merchant?.id) preferPseOverBancolombiaByMerchant.set(merchant.id, true);
                  setDidRedirectToBancolombiaButton(false);
                  setPaymentMethod(pseMethod);
                }}
              >
                Prefiero pagar con PSE
              </Button>
            )}
          </>
        )}
        {paymentMethod.id === 'card' && (
          <Box
            as="form"
            onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}
            display="flex"
            flexDirection="column"
            width="100%"
            gap={4}
          >
            <FormControl isInvalid={!!displayedWarnings.cardNumber}>
              <Text fontWeight={600}>Número de la tarjeta</Text>
              <Input
                variant={'bankSelect'}
                placeholder="0000 - 0000 - 0000 - 0000"
                onChange={paymentInformationHandler('cardNumber')}
                value={cardNumber}
                name="cc-number"
                id="cc-number"
                type="text"
                autoComplete="cc-number"
                inputMode="numeric"
              />
              <FormErrorMessage>{displayedWarnings.cardNumber}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!displayedWarnings.cardExpDate}>
              <Text fontWeight={600}>Fecha de expiración</Text>
              <Input
                variant={'bankSelect'}
                placeholder="MM / AA"
                onChange={paymentInformationHandler('cardExpDate')}
                value={cardExpDate}
                name="cc-exp"
                id="cc-exp"
                autoComplete="cc-exp"
                inputMode="numeric"
              />
              <FormErrorMessage>{displayedWarnings.cardExpDate}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!displayedWarnings.cardCVC}>
              <Text fontWeight={600}>CVC/CVV</Text>
              <Input
                variant={'bankSelect'}
                placeholder="****"
                type="password"
                onChange={paymentInformationHandler('cardCVC')}
                value={cardCVC}
                name="cc-csc"
                id="cc-csc"
                autoComplete="cc-csc"
                inputMode="numeric"
              />
              <FormErrorMessage>{displayedWarnings.cardCVC}</FormErrorMessage>
            </FormControl>
            {!merchant?.disableCardInstallments && (
              <Box>
                <Text fontWeight={600}>Cuotas</Text>
                <Box>
                  <Text fontSize="sm" mb={2}>
                    Diferir a cuotas para <b>tarjeta de crédito local</b>
                  </Text>

                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Switch
                      isChecked={installmentsEnabled}
                      onChange={(e) => {
                        setInstallmentsEnabled(e.target.checked);
                        setInstallments(1);
                      }}
                      colorScheme="teal"
                      size="md"
                      aria-label="Diferir a cuotas"
                    />
                    <Select
                      value={installments}
                      onChange={(e) => setInstallments(Number(e.target.value))}
                      isDisabled={!installmentsEnabled}
                      flex={1}
                    >
                      {[1, 2, 3, 4, 5, 6, 12, 18, 24].map((i) => (
                        <option key={i} value={i}>{`${i} cuota${i === 1 ? '' : 's'}`}</option>
                      ))}
                    </Select>
                  </Box>
                </Box>
              </Box>
            )}
            <Divider borderColor="#DFDEE2" />
            <PersonalInfo
              name={name}
              setName={setName}
              documentType={documentType}
              setDocumentType={setDocumentType}
              documentNumber={documentNumber}
              setDocumentNumber={setDocumentNumber}
              email={email}
              setEmail={setEmail}
              warnings={displayedWarnings}
            />
            {merchant?.cardFeeConfirmation && (
              <Checkbox onChange={(e) => setCardFeeConfirmation(e.target.checked)}>
                Soy consciente y acepto el costo adicional por pago con tarjeta.
              </Checkbox>
            )}
          </Box>
        )}
        {(paymentMethod.id === 'breb' || paymentMethod.id === 'cash') && (
          <PersonalInfo
            name={name}
            setName={setName}
            documentType={documentType}
            setDocumentType={setDocumentType}
            documentNumber={documentNumber}
            setDocumentNumber={setDocumentNumber}
            email={email}
            setEmail={setEmail}
            warnings={displayedWarnings}
          />
        )}
        {requiresPuntosColombia && (
          <Box
            display="flex"
            flexDirection="column"
            gap={4}
            p={4}
            borderWidth="1px"
            borderColor="#DFDEE2"
            borderRadius="xl"
            bg="gray.50"
          >
            <Box display="flex" alignItems="center" gap={3}>
              <Image src={puntosColombiaLogo} alt="Puntos Colombia" boxSize="40px" objectFit="contain" />
              <Box>
                <Text fontSize="sm" fontWeight={700} color="rentals.primary.txt" lineHeight="1.2">
                  Tu cuenta de Puntos Colombia
                </Text>
                <Text fontSize="xs" color="gray.600" mt={0.5} lineHeight="1.3">
                  para acumular tus puntos
                </Text>
              </Box>
            </Box>
            <FormControl isInvalid={!!displayedWarnings.puntosColombiaDocumentType}>
              <Text fontWeight={600}>Tipo de Documento</Text>
              <Select
                placeholder="Seleccionar"
                variant={'bankSelect'}
                bg="white"
                onChange={(e) => {
                  const v = e.target.value;
                  setPuntosColombiaDocumentType(v === '' ? undefined : (v as 'cc' | 'ce' | 'nit'));
                }}
                value={puntosColombiaDocumentType ?? ''}
                isRequired
              >
                <option value={'cc'} style={{ color: '#3C3A45' }}>
                  Cédula de Ciudadanía
                </option>
                <option value={'ce'} style={{ color: '#3C3A45' }}>
                  Cédula de Extranjería
                </option>
                <option value={'nit'} style={{ color: '#3C3A45' }}>
                  NIT
                </option>
              </Select>
              <FormErrorMessage>{displayedWarnings.puntosColombiaDocumentType}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!displayedWarnings.puntosColombiaDocumentNumber}>
              <Text fontWeight={600}>Número de Documento</Text>
              <Input
                variant={'bankSelect'}
                bg="white"
                placeholder="Número de Documento"
                onChange={(val) => setPuntosColombiaDocumentNumber(val.target.value.trim())}
                value={puntosColombiaDocumentNumber}
                inputMode="numeric"
                name="puntos-colombia-document-number"
                id="puntos-colombia-document-number"
                type="text"
              />
              <FormErrorMessage>{displayedWarnings.puntosColombiaDocumentNumber}</FormErrorMessage>
            </FormControl>
          </Box>
        )}
        {merchant?.id && (
          <Checkbox
            variant="main"
            size="md"
            w="100%"
            cursor="pointer"
            borderWidth="1px"
            borderRadius="xl"
            borderColor={rememberPayerInfo ? 'rentals.primary.btn' : 'gray.200'}
            bg={rememberPayerInfo ? 'rgba(22, 18, 54, 0.04)' : 'gray.50'}
            px={4}
            py={3.5}
            alignItems="flex-start"
            spacing={3}
            transition="background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease"
            boxShadow={rememberPayerInfo ? '0 1px 2px rgba(22, 18, 54, 0.06)' : 'none'}
            _hover={{
              borderColor: rememberPayerInfo ? 'rentals.primary.btn' : 'gray.300',
            }}
            sx={{
              '& .chakra-checkbox__control': { marginTop: '3px' },
            }}
            isChecked={rememberPayerInfo}
            onChange={(e) => {
              const checked = e.target.checked;
              setRememberPayerInfo(checked);
              if (!checked) {
                debouncedPersistPayer.cancel();
                clearSavedPayer(merchant.id);
              }
            }}
          >
            <VStack align="stretch" spacing={2} flex={1} minW={0}>
              <Text fontSize="sm" fontWeight="600" color="rentals.primary.txt" lineHeight="1.375">
                Guardar mis datos del pagador en este dispositivo
              </Text>
              {rememberPayerInfo && (
                <Text fontSize="xs" color="gray.600" lineHeight="1.45">
                  Solo en este navegador; podrás borrarlos cuando quieras desmarcando esta opción.
                </Text>
              )}
              {paymentMethod.id === 'card' && (
                <Text fontSize="xs" color="gray.600" lineHeight="1.45">
                  Por tu seguridad, el número, la fecha y el código de la tarjeta no se guardan en este dispositivo.
                </Text>
              )}
            </VStack>
          </Checkbox>
        )}
      </Box>
    </Box>
  );
};

export default PaymentForm;
